<template>
  <div class="d-flex flex-column gap-4">
    <div class="d-flex align-start gap-6 my-4">
      <div class="flex-grow-1">
        <p>
          {{ description }}
        </p>
      </div>

      <preview-as-carrier-button :warehouse="warehouse" />
    </div>

    <div class="setting-input">
      <label class="font-size-small d-flex v-label mb-2">
        <strong class="d-block mr-2">
          {{ settings.includeHandlingUnitsFieldOnAppointmentForms.label }}
        </strong>
      </label>
      <v-select
        v-model="localSettings.includeHandlingUnitsFieldOnAppointmentForms"
        :items="getBoolOptions('includeHandlingUnitsFieldOnAppointmentForms')"
        item-title="label"
        item-value="value"
        variant="outlined"
        density="compact"
        data-testid="include-handling-units-select"
        :disabled="readOnly" />
    </div>

    <template v-if="localSettings.includeHandlingUnitsFieldOnAppointmentForms">
      <div class="setting-input">
        <label class="font-size-small d-flex v-label mb-2">
          <strong class="d-block mr-2">{{ settings.handlingUnitsLabel.label }}</strong>
          <help-icon-tooltip icon-style-class="grey">
            {{ settings.handlingUnitsLabel.description }}
          </help-icon-tooltip>
        </label>
        <v-text-field
          v-model="localSettings.handlingUnitsLabel"
          :placeholder="settings.handlingUnitsLabel.defaultValue"
          :hint="stripHtml(settings.handlingUnitsLabel.note)"
          variant="outlined"
          clearable
          persistent-hint
          persistent-placeholder
          data-testid="handling-units-label-input"
          :disabled="readOnly" />
      </div>

      <div class="setting-input">
        <label class="font-size-small d-flex v-label mb-2">
          <strong class="d-block mr-2">{{ settings.handlingUnitsHelperText.label }}</strong>
          <help-icon-tooltip icon-style-class="grey">
            {{ settings.handlingUnitsHelperText.description }}
          </help-icon-tooltip>
        </label>
        <v-text-field
          v-model="localSettings.handlingUnitsHelperText"
          :placeholder="settings.handlingUnitsHelperText.defaultValue"
          variant="outlined"
          clearable
          persistent-placeholder
          data-testid="handling-units-helper-text-input"
          :disabled="readOnly" />
      </div>

      <div class="setting-input">
        <label class="font-size-small d-flex v-label mb-2">
          <strong class="d-block mr-2">{{ settings.isHandlingUnitsRequired.label }}</strong>
          <help-icon-tooltip icon-style-class="grey">
            {{ settings.isHandlingUnitsRequired.description }}
          </help-icon-tooltip>
        </label>
        <v-select
          v-model="localSettings.isHandlingUnitsRequired"
          :items="getBoolOptions('isHandlingUnitsRequired')"
          item-title="label"
          item-value="value"
          density="compact"
          variant="outlined"
          data-testid="handling-units-required-select"
          :disabled="readOnly" />
      </div>

      <div class="d-flex gap-4">
        <div class="setting-input flex-1">
          <label class="font-size-small d-flex v-label mb-2">
            <strong class="d-block mr-2">{{ settings.handlingUnitsMinimumThreshold.label }}</strong>
            <help-icon-tooltip icon-style-class="grey">
              {{ settings.handlingUnitsMinimumThreshold.description }}
            </help-icon-tooltip>
          </label>
          <v-text-field
            type="number"
            :model-value="localSettings.handlingUnitsMinimumThreshold"
            @update:model-value="localSettings.handlingUnitsMinimumThreshold = Number($event)"
            :placeholder="String(settings.handlingUnitsMinimumThreshold.defaultValue)"
            variant="outlined"
            clearable
            persistent-placeholder
            :error-messages="minThresholdError"
            data-testid="handling-units-min-input"
            :disabled="readOnly" />
        </div>

        <div class="setting-input flex-1">
          <label class="font-size-small d-flex v-label mb-2">
            <strong class="d-block mr-2">{{ settings.handlingUnitsMaximumThreshold.label }}</strong>
            <help-icon-tooltip icon-style-class="grey">
              {{ settings.handlingUnitsMaximumThreshold.description }}
            </help-icon-tooltip>
          </label>
          <v-text-field
            type="number"
            :model-value="localSettings.handlingUnitsMaximumThreshold"
            @update:model-value="localSettings.handlingUnitsMaximumThreshold = Number($event)"
            :placeholder="String(settings.handlingUnitsMaximumThreshold.defaultValue)"
            variant="outlined"
            clearable
            persistent-placeholder
            :error-messages="maxThresholdError"
            data-testid="handling-units-max-input"
            :disabled="readOnly" />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  getTranslatedSettingGroupMetadata,
  getTranslatedSettings,
  SettingGroups
} from '@nova/core';
import PreviewAsCarrierButton from '../../warehouses/components/PreviewAsCarrierButton.vue';

const { t } = useI18n();

const props = defineProps({
  entitySettings: {
    type: Object,
    required: true
  },
  entityParentObj: {
    type: Object,
    required: true
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  minThresholdError: {
    type: String,
    default: undefined
  },
  maxThresholdError: {
    type: String,
    default: undefined
  }
});

const emit = defineEmits(['settingsUpdated']);

const warehouse = computed(() => props.entityParentObj);

const localSettings = computed({
  get() {
    return props.entitySettings;
  },
  set(value) {
    emit('settingsUpdated', value);
  }
});

const settings = computed(() => {
  const allSettings = getTranslatedSettings('Warehouse');
  return {
    includeHandlingUnitsFieldOnAppointmentForms:
      allSettings.includeHandlingUnitsFieldOnAppointmentForms,
    handlingUnitsLabel: allSettings.handlingUnitsLabel,
    handlingUnitsHelperText: allSettings.handlingUnitsHelperText,
    isHandlingUnitsRequired: allSettings.isHandlingUnitsRequired,
    handlingUnitsMinimumThreshold: allSettings.handlingUnitsMinimumThreshold,
    handlingUnitsMaximumThreshold: allSettings.handlingUnitsMaximumThreshold
  };
});

const description = computed(() => {
  const settingGroupMeta = getTranslatedSettingGroupMetadata('warehouse');
  return settingGroupMeta[SettingGroups.HANDLING_UNITS]?.description || '';
});

function stripHtml(text) {
  if (!text) return '';
  return text.replace(/<[^>]*>/g, '');
}

function getBoolOptions(settingKey) {
  const setting = settings.value[settingKey];
  const boolOptions = setting?.booleanDropDownOptions;

  if (!boolOptions) {
    return [
      { label: t('global.basicTerms.yes'), value: true },
      { label: t('global.basicTerms.no'), value: false }
    ];
  }

  return [
    {
      label: boolOptions.whenTrue || t('global.basicTerms.yes'),
      value: true
    },
    {
      label: boolOptions.whenFalse || t('global.basicTerms.no'),
      value: false
    }
  ];
}
</script>

<style scoped>
.preview-container {
  background-color: rgba(0, 0, 0, 0.02);
}

.flex-1 {
  flex: 1;
}
</style>
